import * as React from 'react';
import { Box, FormControl, Select, MenuItem } from '@mui/material';
import { MoneyTextField } from './MoneyInput';
import { MoneyTextFieldRangeProps } from './types';
import './MoneyRangeWithCrDr.css';

type CrDrType = 'CR' | 'DR';

type Props = MoneyTextFieldRangeProps & {
  /** If false, skip min<max validation entirely */
  enforceOrder?: boolean;
  /** If true, width adjusts based on character count */
  dynamicWidth?: boolean;
  /** If true, removes border from input fields */
  disableBorder?: boolean;
  /** If true, removes outline from input fields */
  disableOutline?: boolean;
  /** Position of CR/DR dropdowns - 'left' or 'right' */
  crDrPosition?: 'left' | 'right';
  /** If true, CR = negative, DR = positive. If false, CR = positive, DR = negative */
  swapLogic?: boolean;
  /** Whether to show CR/DR dropdowns */
  showCrDr?: boolean;
  /** Default CR/DR selection for min input */
  defaultMinCrDr?: CrDrType;
  /** Default CR/DR selection for max input */
  defaultMaxCrDr?: CrDrType;
  /** If true, shows negative values in display. If false, always shows positive values */
  showNegativeValues?: boolean;
};

const isEmptyVal = (val: unknown) => val == null || val === '' || val === 0;

export const MoneyRangeWithCrDr: React.FC<Props> = ({
  value,
  onChange,
  currencySymbol = '₹',
  locale = 'en-IN',
  enforceOrder = true,
  dynamicWidth = true,
  crDrPosition = 'right',
  swapLogic = false,
  showCrDr = true,
  defaultMinCrDr = 'CR',
  defaultMaxCrDr = 'CR',
  showNegativeValues = false,
}) => {
  const [minVal, maxVal] = value;
  const [minCrDr, setMinCrDr] = React.useState<CrDrType>(defaultMinCrDr);
  const [maxCrDr, setMaxCrDr] = React.useState<CrDrType>(defaultMaxCrDr);

  // Ref to store timeout IDs for debouncing
  const minWarningTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWarningTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Convert absolute value to signed value based on CR/DR and swapLogic
  const getSignedValue = React.useCallback(
    (absValue: number | null, crDrType: CrDrType): number | null => {
      if (absValue == null) return null;

      const isPositive = swapLogic ? crDrType === 'DR' : crDrType === 'CR';
      return isPositive ? Math.abs(absValue) : -Math.abs(absValue);
    },
    [swapLogic]
  );

  // Convert signed value to absolute value for display
  const getAbsoluteValue = React.useCallback((signedValue: number | null): number | null => {
    if (signedValue == null) return null;
    return Math.abs(signedValue);
  }, []);

  // Get CR/DR type from signed value
  const getCrDrFromValue = React.useCallback(
    (signedValue: number | null): CrDrType => {
      if (signedValue == null) return 'CR';

      const isPositive = signedValue >= 0;
      return swapLogic ? (isPositive ? 'DR' : 'CR') : isPositive ? 'CR' : 'DR';
    },
    [swapLogic]
  );

  // Initialize CR/DR from initial values
  React.useEffect(() => {
    if (minVal != null) {
      const initialMinCrDr = getCrDrFromValue(minVal);
      setMinCrDr(initialMinCrDr);
    }
    if (maxVal != null) {
      const initialMaxCrDr = getCrDrFromValue(maxVal);
      setMaxCrDr(initialMaxCrDr);
    }
  }, []);

  const calculateWidth = React.useCallback(
    (val: number | null) => {
      if (!dynamicWidth) return 120;
      const charCount = String(val || 0).length + 5;
      const minWidth = 10;
      return Math.max(minWidth, charCount) * 8;
    },
    [dynamicWidth]
  );

  const getInputStyles = React.useCallback(
    (val: number | null) => ({
      minWidth: calculateWidth(val),
    }),
    [calculateWidth]
  );

  // Get display value based on showNegativeValues prop
  const getDisplayValue = React.useCallback(
    (signedValue: number | null): number | null => {
      if (signedValue == null) return null;
      return showNegativeValues ? signedValue : Math.abs(signedValue);
    },
    [showNegativeValues]
  );

  const setMin = React.useCallback(
    (v: number | null) => {
      if (!enforceOrder) {
        const signedMin = getSignedValue(v, minCrDr);
        const signedMax = getSignedValue(maxVal, maxCrDr);
        onChange([signedMin, signedMax]);
        return;
      }

      // Clear any existing timeout for min warning
      if (minWarningTimeoutRef.current) {
        clearTimeout(minWarningTimeoutRef.current);
        minWarningTimeoutRef.current = null;
      }

      const currentMaxAbs = getAbsoluteValue(maxVal);
      if (isEmptyVal(currentMaxAbs) || v == null || v < (currentMaxAbs as number)) {
        const signedMin = getSignedValue(v, minCrDr);
        const signedMax = getSignedValue(maxVal, maxCrDr);
        onChange([signedMin, signedMax]);
      } else {
        // Debounce the warning message
        minWarningTimeoutRef.current = setTimeout(() => {
          console.warn('Minimum value must be less than Maximum value');
        }, 1000);
      }
    },
    [enforceOrder, maxVal, minCrDr, maxCrDr, getSignedValue, getAbsoluteValue, onChange]
  );

  const setMax = React.useCallback(
    (v: number | null) => {
      if (!enforceOrder) {
        const signedMin = getSignedValue(minVal, minCrDr);
        const signedMax = getSignedValue(v, maxCrDr);
        onChange([signedMin, signedMax]);
        return;
      }

      // Clear any existing timeout for max warning
      if (maxWarningTimeoutRef.current) {
        clearTimeout(maxWarningTimeoutRef.current);
        maxWarningTimeoutRef.current = null;
      }

      const currentMinAbs = getAbsoluteValue(minVal);
      if (isEmptyVal(currentMinAbs) || v == null || v > (currentMinAbs as number)) {
        const signedMin = getSignedValue(minVal, minCrDr);
        const signedMax = getSignedValue(v, maxCrDr);
        onChange([signedMin, signedMax]);
      } else {
        // Debounce the warning message
        maxWarningTimeoutRef.current = setTimeout(() => {
          console.warn('Maximum value must be greater than Minimum value');
        }, 1000);
      }
    },
    [enforceOrder, minVal, minCrDr, maxCrDr, getSignedValue, getAbsoluteValue, onChange]
  );

  const handleMinCrDrChange = React.useCallback(
    (newMinCrDr: CrDrType) => {
      setMinCrDr(newMinCrDr);

      // Convert current absolute values to new signed values
      const currentMinAbs = getAbsoluteValue(minVal);
      const currentMaxAbs = getAbsoluteValue(maxVal);
      const newSignedMin = getSignedValue(currentMinAbs, newMinCrDr);
      const newSignedMax = getSignedValue(currentMaxAbs, maxCrDr);
      onChange([newSignedMin, newSignedMax]);
    },
    [minVal, maxVal, maxCrDr, getAbsoluteValue, getSignedValue, onChange]
  );

  const handleMaxCrDrChange = React.useCallback(
    (newMaxCrDr: CrDrType) => {
      setMaxCrDr(newMaxCrDr);

      // Convert current absolute values to new signed values
      const currentMinAbs = getAbsoluteValue(minVal);
      const currentMaxAbs = getAbsoluteValue(maxVal);
      const newSignedMin = getSignedValue(currentMinAbs, minCrDr);
      const newSignedMax = getSignedValue(currentMaxAbs, newMaxCrDr);
      onChange([newSignedMin, newSignedMax]);
    },
    [minVal, maxVal, minCrDr, getAbsoluteValue, getSignedValue, onChange]
  );

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (minWarningTimeoutRef.current) {
        clearTimeout(minWarningTimeoutRef.current);
      }
      if (maxWarningTimeoutRef.current) {
        clearTimeout(maxWarningTimeoutRef.current);
      }
    };
  }, []);

  const displayMinValue = getDisplayValue(minVal);
  const displayMaxValue = getDisplayValue(maxVal);

  const minDropdownComponent = showCrDr ? (
    <FormControl size="small" sx={{ minWidth: 45 }} className="money-range-crdr-select">
      <Select
        value={minCrDr}
        onChange={(e) => handleMinCrDrChange(e.target.value as CrDrType)}
        sx={{
          '& .MuiSelect-select': {
            padding: '8px 4px 8px 8px',
            fontSize: '0.875rem',
          },
          '& .MuiSelect-icon': {
            right: '4px',
          },
        }}
      >
        <MenuItem value="CR">CR</MenuItem>
        <MenuItem value="DR">DR</MenuItem>
      </Select>
    </FormControl>
  ) : null;

  const maxDropdownComponent = showCrDr ? (
    <FormControl size="small" sx={{ minWidth: 45 }} className="money-range-crdr-select">
      <Select
        value={maxCrDr}
        onChange={(e) => handleMaxCrDrChange(e.target.value as CrDrType)}
        sx={{
          '& .MuiSelect-select': {
            padding: '8px 4px 8px 8px',
            fontSize: '0.875rem',
          },
          '& .MuiSelect-icon': {
            right: '4px',
          },
        }}
      >
        <MenuItem value="CR">CR</MenuItem>
        <MenuItem value="DR">DR</MenuItem>
      </Select>
    </FormControl>
  ) : null;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {crDrPosition === 'left' && minDropdownComponent}
      <MoneyTextField
        value={displayMinValue}
        onChange={setMin}
        currencySymbol={currencySymbol}
        locale={locale}
        fullWidth={false}
        sx={getInputStyles(displayMinValue)}
        className="money-range-crdr-input"
      />
      {crDrPosition === 'right' && minDropdownComponent}

      <span>to</span>

      {crDrPosition === 'left' && maxDropdownComponent}
      <MoneyTextField
        value={displayMaxValue}
        onChange={setMax}
        currencySymbol={currencySymbol}
        locale={locale}
        fullWidth={false}
        sx={getInputStyles(displayMaxValue)}
        className="money-range-crdr-input"
      />
      {crDrPosition === 'right' && maxDropdownComponent}
    </Box>
  );
};
